/**
 * Canonical aesthetic procedure names and synonym resolution (frontend).
 * Abdominoplasty and tummy tuck are treated as the same procedure everywhere.
 */
export const CANONICAL_ABDOMINOPLASTY = 'Abdominoplasty (Tummy tuck)';

export const AESTHETIC_PROCEDURE_OPTIONS = [
  'Breast augmentation',
  'Breast reduction',
  'Breast lift',
  'Liposuction',
  CANONICAL_ABDOMINOPLASTY,
  'Body contouring',
  'Rhinoplasty',
  'Facelift',
  'Blepharoplasty',
  'Neck lift',
  'Brow lift',
  'Gynecomastia',
  'Brachioplasty',
  'Thigh lift',
  'Arm lift',
  'Brazilian butt lift (BBL)',
  'Mommy makeover',
  'Scar revision',
  'Laser resurfacing',
  'Hair transplant',
  'Other',
] as const;

const PROCEDURE_ALIASES: Record<string, string> = {
  abdominoplasty: CANONICAL_ABDOMINOPLASTY,
  'tummy tuck': CANONICAL_ABDOMINOPLASTY,
  tummytuck: CANONICAL_ABDOMINOPLASTY,
  'abdominoplasty (tummy tuck)': CANONICAL_ABDOMINOPLASTY,
  'tummy tuck (abdominoplasty)': CANONICAL_ABDOMINOPLASTY,
  'mini abdominoplasty': CANONICAL_ABDOMINOPLASTY,
  'full abdominoplasty': CANONICAL_ABDOMINOPLASTY,
  'mini tummy tuck': CANONICAL_ABDOMINOPLASTY,
  'full tummy tuck': CANONICAL_ABDOMINOPLASTY,
};

function compactKey(raw: string): string {
  return raw.trim().toLowerCase().replace(/\s+/g, ' ');
}

export function normalizeAestheticProcedureName(raw?: string | null): string | null {
  if (raw == null) return null;
  const trimmed = String(raw).trim();
  if (!trimmed) return null;
  const key = compactKey(trimmed);
  const canonical = PROCEDURE_ALIASES[key];
  if (canonical) return canonical;
  if (key === compactKey(CANONICAL_ABDOMINOPLASTY)) return CANONICAL_ABDOMINOPLASTY;
  return trimmed;
}

/** Display label for lead list / reports — normalizes known procedure synonyms only. */
export function formatLeadProcedureLabel(
  specialisation?: string | null,
  aestheticProcedureName?: string | null
): string {
  const proc = normalizeAestheticProcedureName(aestheticProcedureName);
  if (proc) return proc;
  const spec = normalizeAestheticProcedureName(specialisation);
  if (spec && AESTHETIC_PROCEDURE_OPTIONS.includes(spec as (typeof AESTHETIC_PROCEDURE_OPTIONS)[number])) {
    return spec;
  }
  return specialisation?.trim() || aestheticProcedureName?.trim() || '';
}

export const BANT_TIER_OPTIONS = ['Hot', 'Warm', 'Cold', 'Bad'] as const;
export type BantTierFilter = (typeof BANT_TIER_OPTIONS)[number] | '__unscored__' | 'all';

export function bantTierBadgeClass(tier?: string | null): string {
  switch (tier) {
    case 'Hot':
      return 'bg-rose-100 text-rose-800 ring-rose-200';
    case 'Warm':
      return 'bg-amber-100 text-amber-900 ring-amber-200';
    case 'Cold':
      return 'bg-sky-100 text-sky-800 ring-sky-200';
    case 'Bad':
      return 'bg-slate-200 text-slate-700 ring-slate-300';
    default:
      return 'bg-gray-100 text-gray-500 ring-gray-200';
  }
}
